<!--
  The radio clock. A real circular hour with proportional segments, so an operator can
  understand the shape of an hour before activating it. Always paired with the precise
  list beside it; neither view is decorative.
-->
<script setup lang="ts">
import { computed } from 'vue';
import { CONTENT_PRESENTATION, type ContentType } from '../onair.js';
import type { RadioClockLabels } from '../labels.js';

export interface ClockElement {
  id: string;
  /** Minute within the hour, 0-59. */
  startMinute: number;
  durationMinutes: number;
  label: string;
  contentType: ContentType;
  isExactTime?: boolean;
}

const props = withDefaults(
  defineProps<{ elements: ClockElement[]; size?: number; labels?: RadioClockLabels }>(),
  { size: 260 },
);

const ariaLabel = computed(
  () => props.labels?.ariaLabel ?? 'Radio clock: the shape of one broadcast hour',
);
const hubLabel = computed(() => props.labels?.hub ?? 'HOUR');
const minuteUnit = computed(() => props.labels?.minuteUnit ?? 'm');

const RADIUS = 46;
const CENTER = 50;

function polar(minute: number, radius: number) {
  // Minute 0 at the top, clockwise, like a studio clock.
  const angle = (minute / 60) * 2 * Math.PI - Math.PI / 2;
  return {
    x: CENTER + radius * Math.cos(angle),
    y: CENTER + radius * Math.sin(angle),
  };
}

const segments = computed(() =>
  props.elements.map((element) => {
    const end = element.startMinute + element.durationMinutes;
    const start = polar(element.startMinute, RADIUS);
    const finish = polar(end, RADIUS);
    const largeArc = element.durationMinutes > 30 ? 1 : 0;
    return {
      ...element,
      path: `M ${CENTER} ${CENTER} L ${start.x} ${start.y} A ${RADIUS} ${RADIUS} 0 ${largeArc} 1 ${finish.x} ${finish.y} Z`,
      token: CONTENT_PRESENTATION[element.contentType].token,
      typeLabel:
        props.labels?.contentTypes?.[element.contentType] ??
        CONTENT_PRESENTATION[element.contentType].label,
    };
  }),
);

const ticks = computed(() => [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55]);

function minuteLabel(minute: number): string {
  return `:${String(minute).padStart(2, '0')}`;
}
</script>

<template>
  <div class="shio-clock">
    <svg :width="size" :height="size" viewBox="0 0 100 100" role="img" :aria-label="ariaLabel">
      <circle :cx="CENTER" :cy="CENTER" :r="RADIUS" class="shio-clock__face" />
      <path
        v-for="segment in segments"
        :key="segment.id"
        :d="segment.path"
        :style="{ fill: `hsl(var(${segment.token}) / 0.6)` }"
        class="shio-clock__segment"
      >
        <title>
          {{ minuteLabel(segment.startMinute) }} {{ segment.label }} ({{ segment.typeLabel }})
        </title>
      </path>
      <line
        v-for="tick in ticks"
        :key="tick"
        :x1="polar(tick, RADIUS - 4).x"
        :y1="polar(tick, RADIUS - 4).y"
        :x2="polar(tick, RADIUS).x"
        :y2="polar(tick, RADIUS).y"
        class="shio-clock__tick"
      />
      <circle :cx="CENTER" :cy="CENTER" r="16" class="shio-clock__hub" />
      <text :x="CENTER" :y="CENTER + 3" class="shio-clock__hub-label">{{ hubLabel }}</text>
    </svg>

    <!-- The precise representation. The circle shows shape; this shows the truth. -->
    <ol class="shio-clock__list">
      <li v-for="segment in segments" :key="segment.id" class="shio-clock__list-item">
        <span class="shio-numeric">{{ minuteLabel(segment.startMinute) }}</span>
        <span
          class="shio-clock__swatch"
          :style="{ background: `hsl(var(${segment.token}))` }"
          aria-hidden="true"
        />
        <span class="shio-clock__label">{{ segment.label }}</span>
        <span class="shio-meta">{{ segment.typeLabel }}</span>
        <span class="shio-meta shio-tabular">{{ segment.durationMinutes }}{{ minuteUnit }}</span>
      </li>
    </ol>
  </div>
</template>

<style scoped>
.shio-clock {
  display: flex;
  gap: var(--shio-space-6);
  align-items: flex-start;
  flex-wrap: wrap;
}

.shio-clock__face {
  fill: hsl(var(--shio-surface-elevated));
  stroke: hsl(var(--shio-border));
  stroke-width: 0.6;
}

.shio-clock__segment {
  stroke: hsl(var(--shio-bg-root));
  stroke-width: 0.5;
}

.shio-clock__tick {
  stroke: hsl(var(--shio-text-muted));
  stroke-width: 0.6;
}

.shio-clock__hub {
  fill: hsl(var(--shio-bg-root));
  stroke: hsl(var(--shio-border));
  stroke-width: 0.6;
}

.shio-clock__hub-label {
  fill: hsl(var(--shio-text-muted));
  font-family: var(--shio-font-display);
  font-size: 6px;
  letter-spacing: 0.12em;
  text-anchor: middle;
}

.shio-clock__list {
  flex: 1;
  min-width: 260px;
  list-style: none;
  margin: 0;
  padding: 0;
}

.shio-clock__list-item {
  display: grid;
  grid-template-columns: 48px 10px 1fr auto auto;
  align-items: center;
  gap: var(--shio-space-3);
  height: var(--shio-row-height);
  border-bottom: var(--shio-border-width) solid hsl(var(--shio-border) / 0.4);
}

.shio-clock__swatch {
  width: 10px;
  height: 10px;
  border-radius: 2px;
}

.shio-clock__label {
  font-size: var(--shio-type-table-size);
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
